"use client";

import type { RefObject } from "react";
import {
  DiagramFrame,
  Guide,
  Label,
  LegendRow,
  Outline,
  SHAPE,
} from "@/components/tools/geometry/diagram";

/*
 * A regular polygon with one interior angle, one exterior angle (between a side
 * extended and the next side) and one central angle marked, and a legend giving each
 * value.
 */

export interface PolygonAngleDiagramProps {
  sides: number;
  title: string;
  svgRef: RefObject<SVGSVGElement | null>;
  labels: { interior: string; exterior: string; central: string };
}

interface Point {
  x: number;
  y: number;
}

const CX = 100;
const CY = 112;
const R = 76;
const LEGEND_X = 206;
const INTERIOR = SHAPE.stroke;
const EXTERIOR = SHAPE.guide;
const CENTRAL = SHAPE.accent;

const angleOf = (from: Point, to: Point) =>
  Math.atan2(to.y - from.y, to.x - from.x);

/** An arc of the given radius about `centre` from angle a1 to angle a2, the short way. */
function arcPath(
  centre: Point,
  radius: number,
  a1: number,
  a2: number
): string {
  let delta = a2 - a1;
  while (delta > Math.PI) {
    delta -= 2 * Math.PI;
  }
  while (delta <= -Math.PI) {
    delta += 2 * Math.PI;
  }
  const start = {
    x: centre.x + radius * Math.cos(a1),
    y: centre.y + radius * Math.sin(a1),
  };
  const end = {
    x: centre.x + radius * Math.cos(a2),
    y: centre.y + radius * Math.sin(a2),
  };
  return `M${start.x} ${start.y} A${radius} ${radius} 0 0 ${delta > 0 ? 1 : 0} ${end.x} ${end.y}`;
}

/** A point `distance` from `centre` along the bisector of angles a1 and a2. */
function bisect(
  centre: Point,
  distance: number,
  a1: number,
  a2: number
): Point {
  let delta = a2 - a1;
  while (delta > Math.PI) {
    delta -= 2 * Math.PI;
  }
  while (delta <= -Math.PI) {
    delta += 2 * Math.PI;
  }
  const mid = a1 + delta / 2;
  return {
    x: centre.x + distance * Math.cos(mid),
    y: centre.y + distance * Math.sin(mid),
  };
}

function ArcSample({ color }: { color: string }) {
  return (
    <path
      d="M0 6 A12 12 0 0 1 20 -2"
      fill="none"
      stroke={color}
      strokeWidth={2.5}
    />
  );
}

export function PolygonAngleDiagram({
  sides,
  title,
  svgRef,
  labels,
}: PolygonAngleDiagramProps) {
  const n = Math.max(3, Math.round(sides));
  const vertex = (k: number): Point => {
    const phi = ((270 - 180 / n + (360 * k) / n) * Math.PI) / 180;
    return { x: CX + R * Math.cos(phi), y: CY - R * Math.sin(phi) };
  };
  const points = Array.from({ length: n }, (_, k) => vertex(k));
  const path = `${points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ")} Z`;
  const centre = { x: CX, y: CY };
  const [v0, v1, v2] = [points[0], points[1], points[2]];
  const toV0 = angleOf(v1, v0);
  const toV2 = angleOf(v1, v2);
  const along = angleOf(v0, v1);
  const markSize = Math.min(24, Math.max(10, 560 / n));
  const extension = {
    x: v1.x + Math.cos(along) * 34,
    y: v1.y + Math.sin(along) * 34,
  };
  const inLabel = bisect(v1, markSize + 12, toV0, toV2);
  const outLabel = bisect(v1, markSize + 12, along, toV2);
  const cLabel = bisect(centre, 30, angleOf(centre, v0), angleOf(centre, v1));
  return (
    <DiagramFrame svgRef={svgRef} title={title}>
      <Outline d={path} />
      <Guide x1={v1.x} x2={extension.x} y1={v1.y} y2={extension.y} />
      <Guide color={CENTRAL} x1={CX} x2={v0.x} y1={CY} y2={v0.y} />
      <Guide color={CENTRAL} x1={CX} x2={v1.x} y1={CY} y2={v1.y} />
      <path
        d={arcPath(v1, markSize, toV0, toV2)}
        fill="none"
        stroke={INTERIOR}
        strokeWidth={2.5}
      />
      <path
        d={arcPath(v1, markSize, along, toV2)}
        fill="none"
        stroke={EXTERIOR}
        strokeWidth={2.5}
      />
      <path
        d={arcPath(centre, 20, angleOf(centre, v0), angleOf(centre, v1))}
        fill="none"
        stroke={CENTRAL}
        strokeWidth={2.5}
      />
      <Label
        anchor="middle"
        color={INTERIOR}
        italic
        x={inLabel.x}
        y={inLabel.y + 4}
      >
        α
      </Label>
      <Label
        anchor="middle"
        color={EXTERIOR}
        italic
        x={outLabel.x}
        y={outLabel.y + 4}
      >
        β
      </Label>
      <Label
        anchor="middle"
        color={SHAPE.ink}
        italic
        x={cLabel.x}
        y={cLabel.y + 4}
      >
        γ
      </Label>
      <LegendRow
        sample={<ArcSample color={INTERIOR} />}
        text={labels.interior}
        x={LEGEND_X}
        y={78}
      />
      <LegendRow
        sample={<ArcSample color={EXTERIOR} />}
        text={labels.exterior}
        x={LEGEND_X}
        y={110}
      />
      <LegendRow
        sample={<ArcSample color={CENTRAL} />}
        text={labels.central}
        x={LEGEND_X}
        y={142}
      />
    </DiagramFrame>
  );
}
